"use client"

import React from "react"
import { motion } from "framer-motion"
import { Card, CardContent, CardFooter } from "@/components/ui/card"
import { Button } from "@/components/ui/button"

export type Appointment = {
  section: string
  title: string
  frontDescription: string
  price: string
  duration: string
  image: string
  backDetails: string[]
  bookingSource?: string // Source string for booking flow (e.g., 'noosa_return', 'free_consult')
  color?: {
    from: string
    via: string
    to: string
  }
}

type AppointmentCardProps = {
  appointment: Appointment
  index: number
  isFlipped: boolean
  onFlip: (index: number, value: boolean) => void
  onBook: (source?: string) => void
}

export function AppointmentCard({ appointment, index, isFlipped, onFlip, onBook }: AppointmentCardProps) {
  return (
    <motion.div
      key={`${appointment.section}-${appointment.title}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, delay: index * 0.1 }}
      className="w-full max-w-sm lg:max-w-xs xl:max-w-sm aspect-square relative"
      style={{ perspective: 1000 }}
    >
      <motion.div
        className="absolute inset-0"
        style={{ transformStyle: "preserve-3d" as React.CSSProperties["transformStyle"] }}
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{ duration: 0.6, ease: "easeInOut" }}
      >
        {/* Front */}
        <div className="absolute inset-0" style={{ backfaceVisibility: "hidden" as React.CSSProperties["backfaceVisibility"] }}>
          <Card className="h-full flex flex-col relative rounded-3xl border border-gray-200 shadow overflow-hidden">
            {/* Colored gradient section - covers top half */}
            <div className="relative h-1/2 overflow-hidden">
              <div 
                className="absolute inset-0" 
                style={{
                  background: 'linear-gradient(to bottom right, rgba(222, 222, 222, 0.5),rgb(53, 59, 33))'
                }}
              />
              {/* Title overlaid at bottom left */}
              <div className="absolute bottom-4 left-4 right-4">
                <h4 className="text-white font-extralight text-xl md:text-2xl xl:text-2xl leading-tight">
                  {appointment.title}
                </h4>
              </div>
              {/* Price/duration pill */}
              <div className="absolute top-4 right-4 px-4 py-2 rounded-full bg-neutral-900/30 backdrop-blur-lg text-white text-sm md:text-base font-light">
                {appointment.price} <span className="opacity-80">|</span> {appointment.duration}
              </div>
            </div>
            
            {/* Content section */}
            <CardContent className="flex-1 flex flex-col gap-2 pt-4 px-4">
              <p className="text-gray-600 leading-relaxed">
                {appointment.frontDescription}{' '}
                <button 
                  onClick={() => onFlip(index, true)} 
                  className="text-[#c5a574] hover:text-[#DBB981] font-medium transition-colors inline"
                >
                  Learn more...
                </button>
              </p>
            </CardContent>
            
            {/* Book Appointment button at bottom right */}
            <div className="absolute bottom-4 right-4">
              <Button 
                variant="outline"
                onClick={() => onBook(appointment.bookingSource || 'card')} 
                className="text-sm border border-gray-600 text-gray-600 hover:bg-gray-50"
              >
                Book Appointment
              </Button>
            </div>
          </Card>
        </div>
        {/* Back */}
        <div className="absolute inset-0" style={{ backfaceVisibility: "hidden" as React.CSSProperties["backfaceVisibility"], transform: "rotateY(180deg)" }}>
          <Card className="h-full flex flex-col relative">
            <button onClick={() => onFlip(index, false)} className="absolute top-3 right-3 text-xs uppercase tracking-wide px-2 py-1 rounded-md bg-black/25 hover:bg-black/40 text-white">Back</button>
            <CardContent className="flex-1 overflow-hidden pt-8">
              <div className="h-full overflow-y-auto pr-1 space-y-4">{appointment.backDetails.map((p, i) => (<p key={i}>{p}</p>))}</div>
            </CardContent>
            <CardFooter className="hidden" />
          </Card>
        </div>
      </motion.div>
    </motion.div>
  )
}

